import {
  Link,
  Outlet,
  createFileRoute,
  useRouterState,
  type LinkProps,
} from "@tanstack/react-router";
import { CreditCard, Heart, KeyRound, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Card } from "~/components/ui/card";
import { cn } from "~/lib/cn";

export const Route = createFileRoute("/_user/settings/profile")({
  staticData: { title: "Profile" },
  component: ProfileLayout,
});

function ProfileLayout() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  const isActiveRoute = (to: LinkProps["to"]) => {
    if (!to || typeof to !== "string") return false;

    if (to === "/settings/profile") {
      return currentPath === to;
    }
    return currentPath.startsWith(to);
  };

  const navigationItems: Array<{
    id: string;
    label: string;
    description: string;
    icon: LucideIcon;
    to: LinkProps["to"];
  }> = [
    {
      id: "general-info",
      label: "General info",
      description: "Name, avatar & contact",
      icon: User,
      to: "/settings/profile",
    },
    {
      id: "password-settings",
      label: "Password",
      description: "Security & credentials",
      icon: KeyRound,
      to: "/settings/profile/password-settings",
    },
    {
      id: "coach-preferences",
      label: "Coach preferences",
      description: "Discovery & matching",
      icon: Heart,
      to: "/settings/profile/coaching-preferences",
    },
    {
      id: "billing",
      label: "Billing",
      description: "Subscriptions & payments",
      icon: CreditCard,
      to: "/settings/profile/billing",
    },
  ];
  return (
    <div className="flex min-h-0 flex-col gap-4 md:flex-row">
      <Card className="h-fit w-full shrink-0 p-4 md:sticky md:top-[calc(var(--app-topbar-h)+1.25rem)] md:w-64 lg:w-72">
        <nav className="flex flex-col gap-1">
          {navigationItems.map((item) => {
            const active = item.to ? isActiveRoute(item.to) : false;
            const Icon = item.icon;
            return (
              <Link
                key={item.id}
                to={item.to}
                className={cn(
                  "group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors duration-200",
                  active
                    ? "bg-purple-50 text-purple-700"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                )}
              >
                <span
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-xl transition-colors",
                    active
                      ? "bg-purple-100 text-purple-700"
                      : "bg-gray-100 text-gray-500 group-hover:bg-gray-200 group-hover:text-gray-700",
                  )}
                >
                  <Icon className="size-4" />
                </span>
                <div className="min-w-0">
                  <p
                    className={cn(
                      "truncate text-sm font-medium",
                      active ? "text-purple-700" : "text-gray-900",
                    )}
                  >
                    {item.label}
                  </p>
                  <p
                    className={cn(
                      "truncate text-xs",
                      active ? "text-purple-600/70" : "text-gray-500",
                    )}
                  >
                    {item.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </nav>
      </Card>
      <div className="min-w-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
